import { useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, CircularProgress, Container } from '@mui/material';
import { COLORS } from '../../theme/themeConstants';
import { useFeaturedRestaurantApi } from '../../Hooks/useFeaturedRestaurantApi';
import { getUrlInfo } from '../../utils/hashUrlUtils';

/**
 * This component handles the automatic return from HyperPay
 * It verifies the payment and redirects to the success page
 */
const PaymentFeaturedHyperPayReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyFeaturedPayment } = useFeaturedRestaurantApi();

  const urlDebugInfo = getUrlInfo();
  console.log('=== Payment Return Page ===');
  console.log('Full URL info:', urlDebugInfo);
  console.log('Search params:', Object.fromEntries(searchParams.entries()));

  const subscriptionId = useMemo(() => searchParams.get('subscriptionId') || '', [searchParams]);
  let checkoutId = useMemo(() =>
      searchParams.get('id') ||
      searchParams.get('checkoutId') ||
      searchParams.get('resourcePath')?.split('/').pop() ||
      '',
      [searchParams]
    );

  let {merchantIdParam, checkoutID} = useMemo(() => {
    let merchant = searchParams.get('merchantId')
    const merchantIDSplit = merchant?.split('?id=')
    if (merchantIDSplit?.length == 2) {
      merchant = merchantIDSplit[0]
      checkoutId = merchantIDSplit[1]
    }
    return {merchantIdParam: merchant || '', checkoutID: checkoutId || ''}
  }, [searchParams]);

  if(checkoutID)
    checkoutId = checkoutID

  const resourcePath = useMemo(() => searchParams.get('resourcePath') || '', [searchParams]);

  console.log('Return Search param data:', { checkoutId, merchantIdParam, subscriptionId });

  useEffect(() => {
    const verifyAndRedirect = async () => {
      // Get merchant ID from params or localStorage
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const merchantId: string = merchantIdParam || userData?.userId;

      if (!merchantId || !checkoutId) {
        console.error('Missing merchant ID or checkout ID');
        navigate(`/payment/featured-store/hyperpay/success?error=missing_params&subscriptionId=${subscriptionId}`);
        return;
      }

      // Save to localStorage for fallback in success page
      try {
        localStorage.setItem('hyperpay_checkout_id', checkoutId);
        localStorage.setItem('hyperpay_merchant_id', merchantId);
        localStorage.setItem('hyperpay_subscription_id', subscriptionId);
        console.log('Saved payment data to localStorage:', { checkoutId, merchantId, subscriptionId });
      } catch (err) {
        console.error('Failed to save to localStorage:', err);
      }

      try {
        console.log('Verifying payment with:', { checkoutId, merchantId, resourcePath });

        // Verify the payment with backend
        const response = await verifyFeaturedPayment({
          checkoutId: String(checkoutId),
          merchantId: String(merchantId)
        });

        // Redirect to success page with the result
        if (response.success) {
          navigate(`/payment/featured-store/hyperpay/success?status=success&subscriptionId=${subscriptionId}&id=${checkoutId}&merchantId=${merchantId}`);
        } else {
          navigate(`/payment/featured-store/hyperpay/success?status=failed&subscriptionId=${subscriptionId}&id=${checkoutId}&merchantId=${merchantId}&message=${encodeURIComponent(response.message || 'Payment verification failed')}`);
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        navigate(`/payment/featured-store/hyperpay/success?status=error&subscriptionId=${subscriptionId}&id=${checkoutId}&merchantId=${merchantId}&message=${encodeURIComponent('Payment verification failed')}`);
      }
    };

    // Start verification immediately
    verifyAndRedirect();
  }, [checkoutId, merchantIdParam, subscriptionId, resourcePath, verifyFeaturedPayment, navigate]);

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%)'
    }}>
      <Container maxWidth="sm">
        <Paper sx={{
          p: 4,
          border: '1px solid #e8eaed',
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          textAlign: 'center'
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            py: 4
          }}>
            <CircularProgress size={56} sx={{ color: COLORS.primary.main }} />

            <Box>
              <Typography variant="h5" sx={{
                fontWeight: 700,
                mb: 1,
                color: 'text.primary'
              }}>
                Processing Your Payment
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
                Please wait while we verify your payment...
              </Typography>

              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Do not close this window
              </Typography>
            </Box>

            {subscriptionId && (
              <Typography variant="caption" sx={{
                color: 'text.secondary',
                opacity: 0.7
              }}>
                Subscription: {subscriptionId}
              </Typography>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PaymentFeaturedHyperPayReturn;


